import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';

export default function SidebarAccountMenu({ onNavClick }) {
  const { user, logout } = useAuth();
  const { effectiveCollapsed } = useSidebar();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function onPointerDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!user) return null;

  const initial = user.name ? user.name.charAt(0).toUpperCase() : '?';
  const accountLabel = user.name || 'Account';

  const goSettings = () => {
    setOpen(false);
    onNavClick?.();
    navigate('/app/settings');
  };

  const signOut = async () => {
    setOpen(false);
    onNavClick?.();
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div
      ref={wrapRef}
      className={`app-sidebar-account-wrap mt-2${open ? ' app-sidebar-account-wrap--open' : ''}`}
    >
      <button
        type="button"
        className={`app-sidebar-account app-sidebar-account--trigger${
          open ? ' app-sidebar-account--open' : ''
        }${effectiveCollapsed ? ' app-sidebar-account--trigger-collapsed' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`${accountLabel} menu`}
        title={effectiveCollapsed ? accountLabel : undefined}
        data-tooltip={effectiveCollapsed ? accountLabel : undefined}
      >
        <span
          className="app-sidebar-account__avatar w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold shrink-0"
          style={{
            background: 'var(--bg-s2)',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border-divider)',
          }}
        >
          {initial}
        </span>
        {!effectiveCollapsed && (
          <div className="app-sidebar-account__meta flex-1 min-w-0 text-left">
            <div
              className="app-sidebar-account__name text-[12px] font-semibold leading-tight"
              style={{ color: 'var(--text-primary)' }}
            >
              {user.name || 'User'}
            </div>
            {user.email && (
              <div
                className="app-sidebar-account__email text-[10.5px] leading-tight mt-0.5"
                style={{ color: 'var(--text-muted)' }}
              >
                {user.email}
              </div>
            )}
          </div>
        )}
      </button>

      {open && (
        <div className="app-sidebar-account-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="app-sidebar-account-menu__item"
            onClick={goSettings}
          >
            <Settings className="app-sidebar-account-menu__icon" strokeWidth={1.75} />
            Settings
          </button>
          <button
            type="button"
            role="menuitem"
            className="app-sidebar-account-menu__item app-sidebar-account-menu__item--danger"
            onClick={signOut}
          >
            <LogOut className="app-sidebar-account-menu__icon" strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
